import {
  X,
  FileText,
  Users,
  MapPin,
  Building2,
  TrendingUp,
  Phone,
  AlertCircle,
  CheckCircle,
  Mail,
} from "lucide-react";
import { useEffect } from "react";

interface RegistrationInstructionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProceed: () => void;
}

export default function RegistrationInstructionsModal({
  isOpen,
  onClose,
  onProceed,
}: RegistrationInstructionsModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-2 backdrop-blur-sm sm:p-4">
      <div className="relative max-h-[95vh] w-full max-w-4xl scale-100 transform overflow-y-auto rounded-2xl bg-white shadow-2xl transition-all duration-300">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#7A1E1E] px-4 py-3 sm:px-6 sm:py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h2 className="text-xl font-bold text-white sm:text-2xl">
                Hostel Registration - Important Information
              </h2>
              <p className="mt-1 text-xs text-gray-200 sm:text-sm">
                Please review the following requirements before proceeding.
              </p>
            </div>
            <button
              onClick={onClose}
              className="rounded-lg p-2 text-white transition-colors hover:bg-white/20"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6">
          {/* Documents & Information Required */}
          <div className="mb-4 sm:mb-8">
            <div className="mb-1 flex items-center gap-2 sm:gap-3">
              <FileText className="h-5 w-5 flex-shrink-0 text-[#7A1E1E] sm:h-6 sm:w-6" />
              <h3 className="text-lg font-bold text-[#7A1E1E] sm:text-xl">
                Documents & Information Required
              </h3>
            </div>
            <p className="mb-4 text-sm text-gray-700 sm:mb-6 sm:text-base">
              Please keep the following documents and information ready before
              starting the registration process:
            </p>

            <div className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2">
              {[
                {
                  icon: <FileText className="h-6 w-6" />,
                  title: "Trust Registration Certificate",
                  description:
                    "Valid registration certificate of the trust/society.",
                },
                {
                  icon: <Users className="h-6 w-6" />,
                  title: "Current Occupancy Details",
                  description:
                    "Number of students, capacity, and facilities available.",
                },
                {
                  icon: <MapPin className="h-6 w-6" />,
                  title: "Location Information",
                  description: "Complete address with landmark details.",
                },
                {
                  icon: <Building2 className="h-6 w-6" />,
                  title: "Property Documents",
                  description:
                    "Ownership or lease documents of hostel premises.",
                },
                {
                  icon: <TrendingUp className="h-6 w-6" />,
                  title: "Financial Records",
                  description:
                    "Last 2 years financial statements (if applicable).",
                },
                {
                  icon: <Phone className="h-6 w-6" />,
                  title: "Contact Person Details",
                  description:
                    "Name, designation, phone, and email of authorized person.",
                },
              ].map(item => (
                <div
                  key={item.title}
                  className="rounded-lg border border-gray-200 bg-[#FFF9F0] p-3 sm:p-4"
                >
                  <div className="flex items-start gap-2 sm:gap-3">
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[#7A1E1E] text-white sm:h-10 sm:w-10">
                      {item.icon}
                    </div>
                    <h4 className="text-sm text-[#7A1E1E] sm:text-base">
                      <span className="font-semibold">{item.title}</span>
                      <p className="text-xs text-gray-700 sm:text-sm">
                        {item.description}
                      </p>
                    </h4>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Important Guidelines */}
          <div className="mb-6 sm:mb-8">
            <div className="mb-4 flex items-center gap-2 sm:gap-3">
              <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[#D4A017] sm:h-8 sm:w-8">
                <AlertCircle className="h-4 w-4 text-white sm:h-5 sm:w-5" />
              </div>
              <h3 className="text-lg font-bold text-[#7A1E1E] sm:text-xl">
                Important Guidelines
              </h3>
            </div>
            <ul className="grid grid-cols-1 gap-x-8 gap-y-2 sm:gap-y-3 md:grid-cols-2">
              {[
                "Ensure all documents are clear and legible",
                "Information provided should be accurate and up-to-date",
                "You will receive confirmation within 3-5 business days",
                "A JBF representative may contact you for verification",
                "Incomplete applications will not be processed",
              ].map(guideline => (
                <li key={guideline} className="flex items-start gap-2 sm:gap-3">
                  <div className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-[#D4A017] sm:mt-1 sm:h-5 sm:w-5">
                    <CheckCircle className="h-2.5 w-2.5 text-white sm:h-3 sm:w-3" />
                  </div>
                  <span className="text-xs text-gray-700 sm:text-sm">
                    {guideline}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Need Help? */}
          <div className="mb-4 rounded-lg border border-gray-200 bg-[#FFF9F0] p-3 sm:mb-6 sm:p-4">
            <div className="mb-2 flex items-center gap-2 sm:gap-3">
              <Mail className="h-4 w-4 flex-shrink-0 text-[#7A1E1E] sm:h-5 sm:w-5" />
              <h3 className="text-sm font-semibold text-[#7A1E1E] sm:text-base">
                Need Help?
              </h3>
            </div>
            <p className="text-xs text-gray-700 sm:text-sm">
              If you have any questions about the registration process, please
              contact us at{" "}
              <a
                href="mailto:info@jgf.org.in"
                className="break-all text-[#7A1E1E] underline hover:text-[#5A1515]"
              >
                info@jgf.org.in
              </a>{" "}
              or call +91 81690 74905.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
            <button
              onClick={onClose}
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50 sm:w-auto sm:px-6 sm:py-3"
            >
              Cancel
            </button>
            <button
              onClick={onProceed}
              className="w-full rounded-lg bg-[#7A1E1E] px-4 py-2.5 text-sm text-white transition-colors hover:bg-[#5A1515] sm:w-auto sm:px-6 sm:py-3"
            >
              I&apos;m Ready - Proceed to Registration
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
